import type { Screen, ScreenId } from "./ScreenManager";
import { GameManager, GameEvent } from "../../core/GameManager";
import {
  PlayerColor, TokenState, GamePhase, DiceValue, TokenPosition, Move,
  AIDifficulty, SAFE_POSITIONS, PLAYER_START_POSITIONS, PATH_LENGTH,
} from "../../core/types";
import { dicePips } from "./assets";
import { audio } from "./sound";
import { Dice3D } from "./Dice3D";

/* ------------------------------------------------------------------ */
/* Board geometry (15x15 grid, cell = 1 unit)                          */
/* ------------------------------------------------------------------ */

type Cell = [number, number];

const PATH_COORDS: Cell[] = buildPath();

function buildPath(): Cell[] {
  const p: Cell[] = [];
  for (let c = 1; c <= 5; c++) p.push([6, c]);
  for (let r = 5; r >= 0; r--) p.push([r, 6]);
  p.push([0, 7]);
  for (let r = 0; r <= 5; r++) p.push([r, 8]);
  for (let c = 9; c <= 14; c++) p.push([6, c]);
  p.push([7, 14]);
  for (let c = 14; c >= 9; c--) p.push([8, c]);
  for (let r = 9; r <= 14; r++) p.push([r, 8]);
  p.push([14, 7]);
  for (let r = 14; r >= 9; r--) p.push([r, 6]);
  for (let c = 5; c >= 0; c--) p.push([8, c]);
  p.push([7, 0]);
  p.push([6, 0]);
  return p;
}

const STRETCH_COORDS: Record<PlayerColor, Cell[]> = {
  [PlayerColor.RED]: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6]],
  [PlayerColor.GREEN]: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7]],
  // Ordered from the stretch entry toward the center!
  [PlayerColor.YELLOW]: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 8]],
  [PlayerColor.BLUE]: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [8, 7]],
};

const BASE_ORIGIN: Record<PlayerColor, Cell> = {
  [PlayerColor.RED]: [0, 0],
  [PlayerColor.GREEN]: [0, 9],
  [PlayerColor.BLUE]: [9, 0],
  [PlayerColor.YELLOW]: [9, 9],
};

const BASE_SLOTS: Cell[] = [
  [1.85, 1.85], [1.85, 4.15], [4.15, 1.85], [4.15, 4.15],
];

const FINISHED_SPOT: Record<PlayerColor, Cell> = {
  [PlayerColor.RED]: [7, 6.58],
  [PlayerColor.GREEN]: [6.58, 7],
  [PlayerColor.YELLOW]: [7, 8.42],
  [PlayerColor.BLUE]: [8.42, 7],
};

/** Arrow on the first win-lane cell, pointing toward the center. */
const STRETCH_ARROW_ROT: Record<PlayerColor, number> = {
  [PlayerColor.RED]: 90,
  [PlayerColor.GREEN]: 180,
  [PlayerColor.YELLOW]: 270,
  [PlayerColor.BLUE]: 0,
};

const FILL: Record<PlayerColor, string> = {
  [PlayerColor.RED]: "#d9364c",
  [PlayerColor.GREEN]: "#27a84f",
  [PlayerColor.YELLOW]: "#eab41f",
  [PlayerColor.BLUE]: "#2f6fd6",
};

/** Mix a hex color toward white (amt > 0) or black (amt < 0). */
function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  const ch = (v: number): number =>
    Math.round((t - v) * p + v);
  return `rgb(${ch((n >> 16) & 255)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
}

const slug = (color: PlayerColor): string => String(color).toLowerCase();

/** Per-color gradients for the new glossy pawn goti (as in new board reference). */
function pawnDefs(colors: PlayerColor[]): string {
  let d = "<defs>";
  colors.forEach((color) => {
    const id = slug(color);
    const f = FILL[color];
    d += `<linearGradient id="pl-body-${id}" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="${shade(f, 0.42)}"/>
      <stop offset=".38" stop-color="${f}"/>
      <stop offset=".82" stop-color="${shade(f, -0.28)}"/>
      <stop offset="1" stop-color="${shade(f, -0.45)}"/>
    </linearGradient>
    <radialGradient id="pl-head-${id}" cx=".32" cy=".28" r=".92">
      <stop offset="0" stop-color="#ffffff"/>
      <stop offset="0.18" stop-color="${shade(f, 0.55)}"/>
      <stop offset=".52" stop-color="${f}"/>
      <stop offset="1" stop-color="${shade(f, -0.38)}"/>
    </radialGradient>
    <radialGradient id="pl-base-${id}" cx=".5" cy=".3" r=".8">
      <stop offset="0" stop-color="${shade(f, 0.25)}"/>
      <stop offset="1" stop-color="${shade(f, -0.32)}"/>
    </radialGradient>`;
  });
  return d + "</defs>";
}

/** New glossy pawn: round head with highlight, tapered body, flat base — like new board refs. */
function pawnSvg(color: PlayerColor): string {
  const id = slug(color);
  const dark = shade(FILL[color], -0.38);
  return `<g transform="translate(0,-0.18)">
    <g class="token__body">
    <g class="token__ring">
      <circle cy="0.24" r="0.44" fill="none" stroke="rgba(255,208,70,0.32)" stroke-width="0.26" stroke-dasharray="0.17 0.115"/>
      <circle cy="0.24" r="0.44" fill="none" stroke="#ffd75e" stroke-width="0.12" stroke-dasharray="0.17 0.115"/>
    </g>
    <ellipse cx="0" cy="0.42" rx="0.27" ry="0.11" fill="rgba(0,0,0,.32)"/>
    <!-- base disc -->
    <ellipse cx="0" cy="0.28" rx="0.26" ry="0.13" fill="url(#pl-base-${id})" stroke="${dark}" stroke-width="0.015"/>
    <ellipse cx="0" cy="0.26" rx="0.22" ry="0.09" fill="rgba(255,255,255,.18)"/>
    <!-- body -->
    <path d="M-0.19 0.26 C -0.20 0.08 -0.11 -0.02 0 -0.02 C 0.11 -0.02 0.20 0.08 0.19 0.26 Z" fill="url(#pl-body-${id})" stroke="${dark}" stroke-width="0.012" stroke-linejoin="round"/>
    <path d="M-0.13 0.14 C -0.13 0.05 -0.06 0.00 0 0.00 C 0.06 0.00 0.13 0.05 0.13 0.14" fill="none" stroke="rgba(255,255,255,.35)" stroke-width="0.02" stroke-linecap="round"/>
    <!-- collar -->
    <ellipse cx="0" cy="-0.02" rx="0.13" ry="0.055" fill="${dark}"/>
    <ellipse cx="0" cy="-0.035" rx="0.11" ry="0.04" fill="${shade(FILL[color], 0.32)}"/>
    <!-- head -->
    <circle cx="0" cy="-0.19" r="0.17" fill="url(#pl-head-${id})" stroke="${shade(FILL[color], -0.22)}" stroke-width="0.012"/>
    <circle cx="-0.052" cy="-0.24" r="0.045" fill="rgba(255,255,255,.92)"/>
    <circle cx="-0.02" cy="-0.22" r="0.022" fill="rgba(255,255,255,.55)"/>
    </g>
  </g>`;
}

/* ------------------------------------------------------------------ */
/* SVG board builder                                                   */
/* ------------------------------------------------------------------ */

const STAR_POINTS = (() => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? 0.34 : 0.15;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(Math.cos(a) * r).toFixed(3)},${(Math.sin(a) * r).toFixed(3)}`);
  }
  return pts.join(" ");
})();

function boardSvg(labels: Partial<Record<PlayerColor, string>> = {}): string {
  const startSet = new Set<number>(Object.values(PLAYER_START_POSITIONS));
  const startOwner = new Map<number, PlayerColor>();
  (Object.keys(PLAYER_START_POSITIONS) as PlayerColor[]).forEach((color) =>
    startOwner.set(PLAYER_START_POSITIONS[color], color),
  );
  const starCells = SAFE_POSITIONS.filter((i) => !startSet.has(i));
  const esc = (s: string): string =>
    s.replace(/[&<>"']/g, (ch) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!),
    );

  let s = `<svg class="board-svg" viewBox="-0.45 -0.45 15.9 15.9" aria-hidden="true">
    <defs>
      <linearGradient id="goldFrame" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#ffe9a8"/><stop offset=".5" stop-color="#f5c542"/>
        <stop offset="1" stop-color="#b8860b"/>
      </linearGradient>
      <radialGradient id="feltGlow" cx=".5" cy=".35" r=".9">
        <stop offset="0" stop-color="#2c3c86"/><stop offset="1" stop-color="#1b2470"/>
      </radialGradient>
    </defs>
    <rect x="-.42" y="-.42" width="15.84" height="15.84" rx=".65" fill="url(#goldFrame)"/>
    <rect x="-.1" y="-.1" width="15.2" height="15.2" rx=".42" fill="#10173f"/>
    <rect x="0" y="0" width="15" height="15" rx=".32" fill="url(#feltGlow)"/>`;

  (Object.keys(BASE_ORIGIN) as PlayerColor[]).forEach((color) => {
    const [r, c] = BASE_ORIGIN[color];
    s += `<g>
      <rect x="${c + 0.22}" y="${r + 0.22}" width="5.56" height="5.56" rx=".7"
            fill="${FILL[color]}" stroke="rgba(255,255,255,.28)" stroke-width=".06"/>
      <rect x="${c + 1.05}" y="${r + 1.05}" width="3.9" height="3.9" rx=".5"
            fill="rgba(255,255,255,.92)"/>
      <rect x="${c + 1.24}" y="${r + 1.24}" width="3.52" height="3.52" rx=".42"
            fill="rgba(0,0,0,.12)"/></g>`;

    // Four recessed seats (boxes) inside the yard — one per goti.
    BASE_SLOTS.forEach(([sr, sc]) => {
      const cx = c + sc;
      const cy = r + sr;
      s += `<g>
        <circle cx="${cx}" cy="${cy + 0.04}" r=".56" fill="rgba(0,0,0,.18)"/>
        <circle cx="${cx}" cy="${cy}" r=".56" fill="${FILL[color]}"
                stroke="rgba(255,255,255,.45)" stroke-width=".05"/>
        <circle cx="${cx}" cy="${cy}" r=".46" fill="#fbfbff"
                stroke="${FILL[color]}" stroke-opacity=".45" stroke-width=".06"/>
      </g>`;
    });
  });

  // Player name plates on all four bases, mirrored symmetrically:
  // upper bases → plate at the TOP of the box, lower bases → plate at the
  // BOTTOM (same style, same size, same offset from the outer edge).
  (Object.keys(BASE_ORIGIN) as PlayerColor[]).forEach((color) => {
    const [r, c] = BASE_ORIGIN[color];
    const name = labels[color];
    if (!name) return;
    const w = Math.min(4.8, Math.max(2.2, name.length * 0.3 + 0.7));
    const y = r > 6 ? r + 5.2 : r + 0.14; // mirrored offset from the outer edge
    s += `<g>
      <rect x="${c + 3 - w / 2}" y="${y}" width="${w.toFixed(2)}" height="0.66" rx=".33"
            fill="rgba(16,23,63,.85)" stroke="${FILL[color]}" stroke-width=".07"/>
      <circle cx="${c + 3 - w / 2 + 0.42}" cy="${y + 0.33}" r="0.17" fill="${FILL[color]}"
              stroke="rgba(255,255,255,.7)" stroke-width=".04"/>
      <text x="${c + 3 + 0.18}" y="${y + 0.48}" text-anchor="middle" font-family="Nunito, sans-serif"
            font-weight="800" font-size="0.4" fill="#ffffff">${esc(name)}</text>
    </g>`;
  });

  // Progress % pill per player (like new board: dark pill at inner edge)
  (Object.keys(BASE_ORIGIN) as PlayerColor[]).forEach((color) => {
    const [r, c] = BASE_ORIGIN[color];
    const bottom = r > 6;
    const y = bottom ? r + 0.98 : r + 5.66;
    const pillW = 1.3, pillH = 0.52;
    s += `<g class="base-progress" data-color="${slug(color)}">
      <rect x="${(c + 3 - pillW/2).toFixed(2)}" y="${(y - 0.38).toFixed(2)}" width="${pillW}" height="${pillH}" rx="0.26"
            fill="rgba(0,0,0,0.42)" stroke="rgba(255,255,255,0.18)" stroke-width="0.03"/>
      <text x="${c + 3}" y="${(y + 0.02).toFixed(2)}" text-anchor="middle" font-family="Nunito, sans-serif"
            font-weight="900" font-size="0.40" fill="#ffffff">0%</text>
    </g>`;
  });

  for (let i = 0; i < PATH_LENGTH; i++) {
    const [r, c] = PATH_COORDS[i];
    if (startSet.has(i)) {
      // Colored start cell for the matching player.
      s += `<rect x="${c}" y="${r}" width="1" height="1" fill="${FILL[startOwner.get(i)!]}"
        stroke="rgba(255,255,255,.4)" stroke-width=".05"/>`;
      continue;
    }
    const star = starCells.includes(i);
    s += `<rect x="${c}" y="${r}" width="1" height="1" fill="#f4f6fb" stroke="#c9cede" stroke-width=".04"/>
      ${star ? `<polygon points="${STAR_POINTS}" transform="translate(${c + 0.5},${r + 0.5})" fill="#aeb7d2"/>` : ""}`;
  }

  // Win-lane arrows: on the first stretch cell, pointing to the center.
  (Object.keys(STRETCH_ARROW_ROT) as PlayerColor[]).forEach((color) => {
    const [r, c] = STRETCH_COORDS[color][0];
    s += `<polygon points="-0.24,-0.38 0.38,0 -0.24,0.38" transform="translate(${c + 0.53},${r + 0.5}) rotate(${STRETCH_ARROW_ROT[color]})"
      fill="rgba(255,255,255,.9)" stroke="rgba(0,0,0,.18)" stroke-width=".04" stroke-linejoin="round"/>`;
  });

  (Object.keys(STRETCH_COORDS) as PlayerColor[]).forEach((color) => {
    STRETCH_COORDS[color].forEach(([r, c]) => {
      s += `<rect x="${c}" y="${r}" width="1" height="1" fill="${FILL[color]}"
        stroke="rgba(255,255,255,.35)" stroke-width=".05"/>`;
    });
  });

  s += `<g stroke="#10173f" stroke-width=".05">
    <polygon points="6,6 9,6 7.5,7.5" fill="${FILL[PlayerColor.GREEN]}"/>
    <polygon points="9,6 9,9 7.5,7.5" fill="${FILL[PlayerColor.YELLOW]}"/>
    <polygon points="9,9 6,9 7.5,7.5" fill="${FILL[PlayerColor.BLUE]}"/>
    <polygon points="6,9 6,6 7.5,7.5" fill="${FILL[PlayerColor.RED]}"/>
  </g>
  <circle cx="7.5" cy="7.5" r=".26" fill="url(#goldFrame)" opacity=".95"/>
  </svg>`;
  return s;
}

/* ------------------------------------------------------------------ */

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Global goti size multiplier — same pawn art, drawn bigger. */
const TOKEN_SCALE = 1.7;

/** Crossed-swords kill counter icon (per-player tray stat). */
const SWORDS_SVG = `
<svg viewBox="0 0 24 24" aria-hidden="true">
  <path d="M3 3l7.2 7.2M21 3l-7.2 7.2M3 21l6-6M21 21l-6-6"
        stroke="#e8ecf7" stroke-width="2.1" stroke-linecap="round" fill="none"/>
  <path d="M4.2 1.8 1.8 4.2l3 3 2.4-2.4zM19.8 1.8l2.4 2.4-3 3-2.4-2.4zM4.2 22.2l-2.4-2.4 3-3 2.4 2.4zM19.8 22.2l2.4-2.4-3-3-2.4 2.4z"
        fill="#f5c542"/>
</svg>`;

/** Skull death counter icon (per-player tray stat). */
const SKULL_SVG = `
<svg viewBox="0 0 24 24" aria-hidden="true">
  <path d="M12 2a8 8 0 0 0-8 8c0 2.8 1.4 5.2 3.5 6.6V20a1.6 1.6 0 0 0 1.6 1.6h5.8A1.6 1.6 0 0 0 16.5 20v-3.4A8 8 0 0 0 20 10a8 8 0 0 0-8-8z"
        fill="#e8ecf7"/>
  <circle cx="8.8" cy="10.4" r="2" fill="#1b2470"/>
  <circle cx="15.2" cy="10.4" r="2" fill="#1b2470"/>
  <path d="M10.4 15.2 12 13l1.6 2.2-1.6 1.4z" fill="#1b2470"/>
  <rect x="9.6" y="18.2" width="1.5" height="2.6" rx="0.7" fill="#1b2470"/>
  <rect x="12.9" y="18.2" width="1.5" height="2.6" rx="0.7" fill="#1b2470"/>
</svg>`;

export interface MatchConfig {
  colors: PlayerColor[];
  names: string[];
  isAI: boolean[];
}

export interface GameScreenCallbacks {
  onExit: () => void;
  toast: (msg: string) => void;
}

type UiKind = "idle-roll" | "rolling" | "pick" | "anim" | "over";

export class GameScreen implements Screen {
  readonly id: ScreenId = "game";

  private game = new GameManager();
  private cfg: MatchConfig;
  private cb: GameScreenCallbacks;

  private tokenLayer!: SVGGElement;
  private statusEl!: HTMLElement;
  private diceBtn!: HTMLButtonElement;
  private diceFace!: HTMLElement;
  private pauseEl!: HTMLElement;
  private resultEl!: HTMLElement;
  private confirmEl!: HTMLElement;
  private confirmCb: (() => void) | null = null;

  private tokenEls: SVGGraphicsElement[][] = [];
  private validMoves: number[] = [];
  private state: UiKind = "anim";
  private rollInterval = 0;
  private aiTimer = 0;
  private placementAutoTimer = 0;
  private snapshot = "";
  private dice3d: Dice3D | null = null;
  private paused = false;
  /** Corner dice trays + live stats, keyed by board color. */
  private trays = new Map<PlayerColor, HTMLElement>();
  private progressEls = {} as Record<PlayerColor, SVGTextElement>;
  private kills: number[] = [0, 0, 0, 0];
  private deaths: number[] = [0, 0, 0, 0];

  /** Serializes event handling so animations never overlap. */
  private tail: Promise<void> = Promise.resolve();
  private offEvents: (() => void) | null = null;

  /** Winner celebration — isolated overlay (never touches font system). */
  private celebrationEl: HTMLElement | null = null;
  private celebrationTimers: number[] = [];

  constructor(cfg: MatchConfig, cb: GameScreenCallbacks) {
    this.cfg = cfg;
    this.cb = cb;
  }

  /* ---------------- rendering ---------------- */

  /** Player name shown on the plate of their base box. */
  private labels(): Partial<Record<PlayerColor, string>> {
    const out: Partial<Record<PlayerColor, string>> = {};
    this.cfg.colors.forEach((color, i) => {
      out[color] = this.cfg.names[i];
    });
    return out;
  }

  render(): HTMLElement {
    const el = document.createElement("div");
    el.innerHTML = `
      <div class="game">
        <header class="game__topbar">
          <button class="icon-btn icon-btn--sm js-pause" aria-label="Pause game">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
                 stroke-linecap="round"><line x1="9" y1="5" x2="9" y2="19"/>
              <line x1="15" y1="5" x2="15" y2="19"/></svg>
          </button>
        </header>

        <div class="board-area">
          <div class="tray-row tray-row--top">
            ${this.trayHtml(PlayerColor.RED, "left")}
            ${this.trayHtml(PlayerColor.GREEN, "right")}
          </div>

          <div class="board-wrap">
            ${boardSvg(this.boardLabels())}
            <svg class="token-layer" viewBox="-0.45 -0.45 15.9 15.9"></svg>
          </div>

          <div class="tray-row tray-row--bottom">
            ${this.trayHtml(PlayerColor.BLUE, "left")}
            ${this.trayHtml(PlayerColor.YELLOW, "right")}
          </div>
        </div>

        <footer class="dock">
          <div class="status" aria-live="polite"></div>
        </footer>

        <div class="overlay result-overlay" hidden>
          <div class="winner-card panel">
            <div class="winner-card__trophy js-result-art">
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <path d="M20 10h24v10a12 12 0 0 1-24 0z" fill="#f5c542"/>
                <path d="M20 13h-7a1 1 0 0 0-1 1c0 5.5 3.2 8.8 7.4 10" fill="none" stroke="#f5c542" stroke-width="3.4"/>
                <path d="M44 13h7a1 1 0 0 1 1 1c0 5.5-3.2 8.8-7.4 10" fill="none" stroke="#f5c542" stroke-width="3.4"/>
                <rect x="29" y="31" width="6" height="8" fill="#c8951d"/>
                <rect x="21" y="39" width="22" height="6" rx="2.4" fill="#f5c542"/>
                <text x="32" y="21.5" text-anchor="middle" font-size="12" font-weight="900" fill="#7a5200">1</text>
              </svg>
            </div>
            <h2 class="winner-card__title js-result-title">Winner!</h2>
            <p class="winner-card__name js-result-name"></p>
            <div class="standings js-standings"></div>
            <div class="winner-card__actions js-result-actions"></div>
          </div>
        </div>

        <div class="overlay confirm-overlay" hidden>
          <div class="winner-card panel">
            <h2 class="winner-card__title">Are you sure?</h2>
            <p class="confirm-overlay__msg js-confirm-msg"></p>
            <div class="winner-card__actions">
              <button class="btn btn--green js-confirm-yes">Yes</button>
              <button class="btn btn--gold js-confirm-no">No</button>
            </div>
          </div>
        </div>

        <div class="overlay pause-overlay" hidden>
          <div class="winner-card panel">
            <div class="winner-card__trophy" aria-hidden="true"><svg viewBox="0 0 24 24" width="56" height="56"><rect x="3" y="3" width="18" height="18" rx="4" fill="#f5c542" stroke="#b8860b" stroke-width="1.4"/><path d="M8 8h3v8H8zM13 8h3v8h-3z" fill="#1b2470"/></svg></div>
            <h2 class="winner-card__title">Game <span class="t-orange">Paused</span></h2>
            <p class="winner-card__name">Take a break — tap Resume to continue</p>
            <div class="winner-card__actions">
              <button class="btn btn--green js-resume"><span>RESUME</span><span class="btn__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="9 6 17 12 9 18" fill="currentColor" stroke="none"/></svg></span></button>
              <button class="btn btn--gold js-quit"><span>HOME</span><span class="btn__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10L12 3l9 7"/><path d="M9 21V11h6v10"/></svg></span></button>
            </div>
          </div>
        </div>
      </div>
    `;
    this.tokenLayer = el.querySelector(".token-layer")!;
    this.statusEl = el.querySelector(".status")!;
    this.pauseEl = el.querySelector(".pause-overlay")!;
    this.resultEl = el.querySelector(".result-overlay")!;
    this.confirmEl = el.querySelector(".confirm-overlay")!;

    // Corner trays + base % pills, keyed by board color.
    (Object.keys(BASE_ORIGIN) as PlayerColor[]).forEach((color) => {
      const tray = el.querySelector<HTMLElement>(`.tray[data-color="${slug(color)}"]`);
      if (tray) this.trays.set(color, tray);
      const pct = el.querySelector<SVGElement>(`.base-progress[data-color="${slug(color)}"]`) as unknown as SVGTextElement;
      if (pct) this.progressEls[color] = pct;
    });

    // The dice lives inside the active player's tray box and hops between
    // trays as turns pass.
    const dice = document.createElement("button");
    dice.className = "dice-lg";
    dice.setAttribute("aria-label", "Roll the dice");
    dice.disabled = true;
    dice.innerHTML = `<span class="dice-lg__face">${dicePips(faceFor(1))}</span>
      <span class="dice3d-mount"></span>`;
    this.diceBtn = dice;
    this.diceFace = dice.querySelector(".dice-lg__face")!;
    this.trays.get(this.cfg.colors[0])?.querySelector(".tray__slot")?.appendChild(dice);

    el.querySelector(".js-pause")!.addEventListener("click", () => this.onPauseClick());
    this.diceBtn.addEventListener("click", () => this.onRollClick());
    el.querySelector(".js-resume")!.addEventListener("click", () => this.onResumeClick());
    el.querySelector(".js-quit")!.addEventListener("click", () =>
      this.confirmAction("Quit the match and go home?", () => {
        this.pauseEl.hidden = true;
        this.onExitClick();
      }),
    );
    el.querySelector(".js-confirm-yes")!.addEventListener("click", () => {
      this.confirmEl.hidden = true;
      const cb = this.confirmCb;
      this.confirmCb = null;
      cb?.();
    });
    el.querySelector(".js-confirm-no")!.addEventListener("click", () => {
      this.confirmEl.hidden = true;
      this.confirmCb = null;
    });

    this.buildTokens();
    return el;
  }

  onEnter(): void {
    // Board is silent by design — kill menu music first thing, no matter
    // which timing path got us here (slow load, ad dismiss, rematch).
    audio.stopMusic();
    const handler = (e: GameEvent) => this.push(() => this.handleEvent(e));
    this.game.on(handler);
    this.offEvents = () => this.game.off(handler);
    this.startMatch();
    void this.requestWakeLock();

    // Minimized / switched apps: freeze the match behind the pause card.
    document.addEventListener("visibilitychange", this.onVisibility);

    // Mount the 3D dice (three.js) over the flat face once the DOM is live.
    // Loaded via dynamic import — keeps three.js OUT of the startup bundle
    // so the app boots instantly. Failure never blocks the game — the SVG
    // face stays as fallback.
    const mount = this.diceBtn.querySelector<HTMLElement>(".dice3d-mount")!;
    void import("./Dice3D")
      .then(({ Dice3D }) => {
        if (!mount.isConnected) return; // left the game before it loaded
        const dice3d = new Dice3D(mount);
        this.dice3d = dice3d;
        void dice3d.ready.then((ok) => {
          if (!ok || this.dice3d !== dice3d) {
            dice3d.dispose();
            if (this.dice3d === dice3d) this.dice3d = null;
          } else {
            this.diceBtn.classList.add("has-3d");
          }
        });
      })
      .catch((err) => console.warn("3D dice unavailable:", err));
  }

  onExit(): void {
    this.offEvents?.();
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.clearInterval(this.rollInterval);
    window.clearTimeout(this.aiTimer);
    window.clearTimeout(this.placementAutoTimer);
    this.clearCelebration();
    this.dice3d?.dispose();
    this.dice3d = null;
    void this.releaseWakeLock();
  }

  /** Screen Wake Lock (web/PWA layer — native FLAG_KEEP_SCREEN_ON in
      MainActivity is the Android layer). Both are no-op-safe. */
  private wakeLock: { release(): Promise<void> } | null = null;

  private async requestWakeLock(): Promise<void> {
    try {
      const nav = navigator as Navigator & {
        wakeLock?: { request(kind: string): Promise<{ release(): Promise<void> }> };
      };
      if (!nav.wakeLock?.request) return;
      this.wakeLock = await nav.wakeLock.request('screen');
    } catch {
      this.wakeLock = null; // denied/unsupported — native flag still holds
    }
  }

  private async releaseWakeLock(): Promise<void> {
    try {
      await this.wakeLock?.release();
    } catch { /* already released */ }
    this.wakeLock = null;
  }

  /** Auto-pause when the app goes to the background. */
  private onVisibility = (): void => {
    if (document.hidden) {
      this.onPauseClick();
    } else {
      // Wake lock auto-releases on hide — re-acquire on return.
      void this.requestWakeLock();
    }
  };

  /** Player names keyed by board color, for the base name plates. */
  private boardLabels(): Partial<Record<PlayerColor, string>> {
    const out: Partial<Record<PlayerColor, string>> = {};
    this.cfg.colors.forEach((color, i) => {
      out[color] = this.cfg.names[i];
    });
    return out;
  }

  private startMatch(): void {
    this.kills = this.cfg.colors.map(() => 0);
    this.deaths = this.cfg.colors.map(() => 0);
    this.game.startGame({
      players: this.cfg.colors.map((color, i) => ({
        color,
        name: this.cfg.names[i],
        isAI: this.cfg.isAI[i],
        aiDifficulty: AIDifficulty.NORMAL,
      })),
      maxPlayers: this.cfg.colors.length,
    });
  }

  /* ---------------- event queue ---------------- */

  private push(fn: () => void | Promise<void>): void {
    this.tail = this.tail.then(fn).catch((err) => console.error(err));
  }

  private async handleEvent(e: GameEvent): Promise<void> {
    switch (e.type) {
      case "turn_change":
        if (this.game.phase === GamePhase.GAME_OVER) break;
        this.clearHighlights();
        this.syncAll();
        // While the King/placement card is up (mid-game win in 3P/4P) the
        // board still passes the turn behind the overlay — but the dice
        // must NOT arm yet. Arming happens only after CONTINUE (resumeGame)
        // so human dice doesn't appear tappable behind the modal and AI
        // doesn't start thinking under the card (fixes 3P King not seen).
        if (!this.resultEl.hidden) break;
        if (this.paused) break;
        await this.afterTurnChange();
        break;
      case "extra_turn": {
        audio.play("extra_turn");
        const reason = e.data?.reason as string | undefined;
        if (reason === "capture") {
          this.setStatus(`${this.nameOf(this.game.currentPlayerIndex)} captured — roll again!`);
        } else if (reason === "finish") {
          this.setStatus(`${this.nameOf(this.game.currentPlayerIndex)} home — roll again!`);
        } else {
          this.setStatus(`${this.nameOf(this.game.currentPlayerIndex)} rolls again!`);
        }
        if (this.isHuman(this.game.currentPlayerIndex)) {
          this.armRoll();
        } else {
          this.scheduleAiRoll(700);
        }
        break;
      }
      case "three_sixes":
        // Turn is forfeited silently — no toast popping over the board.
        audio.play("error");
        break;
      case "move_start":
        this.state = "anim";
        break;
      case "move_end":
        await this.animateMove(e.data!.move as Move);
        break;
      case "capture":
        audio.play("capture");
        this.setStatus(`${this.nameOf(e.data!.playerIndex as number)} captured a token!`);
        break;
      case "token_finished":
        audio.play("token_home");
        this.setStatus(`${this.nameOf(e.data!.playerIndex as number)} got a token home!`);
        break;
      case "dice_roll_start":
        audio.play("dice_roll");
        this.startDiceSpin();
        // Tumble the instant the roll begins — no dead time after the tap.
        this.dice3d?.startTumble();
        break;
      case "dice_roll_end": {
        const v = e.data!.value as DiceValue;
        this.stopDiceSpin(v);
        await this.dice3d?.settle(v);
        break;
      }
      case "game_over":
        audio.play("win");
        audio.playWinLoop();
        this.showFinalStandings(e.data!.rankings as number[]);
        break;
      case "player_ranked": {
        const rank = e.data!.rank as number;
        this.updateRankBadges();
        this.showPlacementCard(
          e.data!.playerIndex as number,
          rank,
        );
        break;
      }
      default:
        break;
    }
  }

  /* ---------------- flow helpers ---------------- */

  private nameOf(i: number): string {
    return this.game.players[i]?.config.name ?? `Player ${i + 1}`;
  }

  private isHuman(i: number): boolean {
    return !this.game.players[i]?.config.isAI;
  }

  private afterTurnChange(): void {
    const idx = this.game.currentPlayerIndex;
    if (!this.isHuman(idx)) {
      this.setDiceEnabled(false);
      this.setStatus(`${this.nameOf(idx)} is thinking…`);
      this.scheduleAiRoll(750);
      this.state = "anim";
    } else {
      this.armRoll();
    }
  }

  private scheduleAiRoll(delay = 750): void {
    window.clearTimeout(this.aiTimer);
    this.aiTimer = window.setTimeout(() => {
      if (
        !this.paused &&
        this.state !== "over" &&
        !this.isHuman(this.game.currentPlayerIndex)
      ) {
        this.snapshot = this.snapshotPositions();
        void this.game.rollDice();
      }
    }, delay);
  }

  private armRoll(): void {
    if (this.game.phase !== GamePhase.PLAYING) return;
    this.setDiceEnabled(true);
    this.setStatus(`${this.nameOf(this.game.currentPlayerIndex)} — tap to roll`);
    this.state = "idle-roll";
  }

  private onRollClick(): void {
    if (this.paused || this.state !== "idle-roll") return;
    const idx = this.game.currentPlayerIndex;
    if (!this.isHuman(idx)) return;
    this.setDiceEnabled(false);
    this.state = "rolling";
    void this.game.rollDice().then(() => this.afterHumanRoll(idx));
  }

  private afterHumanRoll(rollOwner: number): void {
    const value = this.game.diceValue;
    if (value === null || this.state !== "rolling") return;
    // A no-move roll skips the turn synchronously inside rollDice — the
    // queued turn_change owns the flow now, so don't touch anything.
    if (this.game.currentPlayerIndex !== rollOwner) return;
    if (!this.isHuman(rollOwner)) return;
    const moves = this.game.getValidMoves(value);
    if (moves.length === 0) return; // no-move case resolves via turn_change

    // Only ONE legal move (e.g. the last goti needing an exact roll to
    // finish)? Play it automatically — no pointless tap required.
    if (moves.length === 1) {
      this.snapshot = this.snapshotPositions();
      this.state = "anim";
      this.setStatus(`${this.nameOf(rollOwner)} — only one move, playing it`);
      void this.game.executeMove(rollOwner, moves[0], value);
      return;
    }

    // Several options — glow every movable token and wait for the pick.
    this.validMoves = moves;
    this.highlightMoves(moves);
    this.setStatus(`You rolled ${value} — pick a glowing token`);
    this.state = "pick";
  }

  private onTokenClick(playerIdx: number, tokenIdx: number): void {
    if (this.paused || this.state !== "pick") return;
    if (playerIdx !== this.game.currentPlayerIndex) return;
    if (!this.validMoves.includes(tokenIdx)) return;
    const value = this.game.diceValue;
    if (value === null) return;
    this.clearHighlights();
    this.snapshot = this.snapshotPositions();
    this.state = "anim";
    void this.game.executeMove(playerIdx, tokenIdx, value);
  }

  private rematch(): void {
    audio.stopWinLoop();
    this.clearCelebration();
    window.clearTimeout(this.placementAutoTimer);
    this.resultEl.hidden = true;
    this.confirmEl.hidden = true;
    this.paused = false;
    this.state = "anim";
    this.startMatch(); // clears rankings first…
    this.updateRankBadges(); // …so this wipes the old 1st/2nd yard badges
  }

  /* ---------------- pause ---------------- */

  private onPauseClick(): void {
    if (this.state === "over" || this.paused) return;
    this.paused = true;
    window.clearTimeout(this.aiTimer); // AI waits while paused
    this.pauseEl.hidden = false;
  }

  private onResumeClick(): void {
    if (!this.paused) return;
    this.paused = false;
    this.pauseEl.hidden = true;
    // Resume the AI's turn if it was thinking when paused.
    const idx = this.game.currentPlayerIndex;
    if (
      this.game.phase === GamePhase.PLAYING &&
      !this.isHuman(idx) &&
      (this.game.diceValue === null || this.state === "anim")
    ) {
      this.scheduleAiRoll(600);
    }
  }

  private onExitClick = (): void => {
    audio.stopWinLoop();
    this.clearCelebration();
    this.cb.onExit();
  };

  /* ---------------- tokens & animation ---------------- */

  private buildTokens(): void {
    this.tokenLayer.innerHTML = pawnDefs(this.cfg.colors);
    this.tokenEls = this.cfg.colors.map((color) =>
      Array.from({ length: 4 }, (_, i) => {
        const g = document.createElementNS("http://www.w3.org/2000/svg", "g") as SVGGraphicsElement;
        g.classList.add("token");
        g.innerHTML = pawnSvg(color);
        g.addEventListener("click", () =>
          this.onTokenClick(this.cfg.colors.indexOf(color), i),
        );
        this.tokenLayer.appendChild(g);
        return g;
      }),
    );
    this.syncAll();
  }

  private place(el: SVGGraphicsElement, cell: Cell, scale = 1, dy = 0): void {
    const s = scale * TOKEN_SCALE;
    el.style.transform =
      `translate(${cell[1]}px, ${cell[0] + dy}px)` +
      (s !== 1 ? ` scale(${s})` : "");
  }

  private coordOf(playerIdx: number, tokenIdx: number): { cell: Cell; stackKey: string } {
    const color = this.cfg.colors[playerIdx];
    const t = this.game.players[playerIdx].tokens[tokenIdx];
    switch (t.state) {
      case TokenState.HOME: {
        const [or, oc] = BASE_ORIGIN[color];
        const [r, c] = BASE_SLOTS[tokenIdx];
        return { cell: [or + r, oc + c], stackKey: `home-${playerIdx}-${tokenIdx}` };
      }
      case TokenState.ON_PATH: {
        const [r, c] = PATH_COORDS[t.pathIndex];
        return { cell: [r + 0.5, c + 0.5], stackKey: `p${t.pathIndex}` };
      }
      case TokenState.HOME_STRETCH: {
        const [r, c] = STRETCH_COORDS[color][t.stretchIndex];
        return { cell: [r + 0.5, c + 0.5], stackKey: `s${t.pathIndex}-${t.stretchIndex}` };
      }
      case TokenState.FINISHED: {
        // All finished gotis share one spot + stack key, so the existing
        // fan-out/shrink grouping merges them into a tight cluster.
        const [r, c] = FINISHED_SPOT[color];
        return { cell: [r, c], stackKey: `fin-${playerIdx}` };
      }
    }
  }

  private syncAll(): void {
    const groups = new Map<string, { playerIdx: number; tokenIdx: number }[]>();
    const keys: string[][] = [];
    for (let p = 0; p < this.game.players.length; p++) {
      keys[p] = [];
      for (let t = 0; t < 4; t++) {
        const { stackKey } = this.coordOf(p, t);
        keys[p][t] = stackKey;
        const list = groups.get(stackKey) ?? [];
        list.push({ playerIdx: p, tokenIdx: t });
        groups.set(stackKey, list);
      }
    }
    for (let p = 0; p < this.game.players.length; p++) {
      for (let t = 0; t < 4; t++) {
        const group = groups.get(keys[p][t])!;
        const slot = group.findIndex((g) => g.playerIdx === p && g.tokenIdx === t);
        const n = group.length;
        const { cell } = this.coordOf(p, t);
        // Stacked gotis fan out diagonally and shrink, so every head
        // stays visible instead of one body swallowing the other.
        // Bigger groups spread less and shrink more — 4 gotis stay
        // inside their cell.
        const off = slot - (n - 1) / 2;
        const spread = n > 2 ? 0.26 : 0.34;
        const jx = n > 1 ? off * spread : 0;
        const jy = n > 1 ? -off * spread * 0.7 : 0;
        const scale = n > 1 ? Math.max(0.55, 1.06 - n * 0.16) : 1;
        // Yard gotis drop back down so the body sits inside the seat
        // circle; lane gotis stay standing on their cell.
        const dy = keys[p][t].startsWith("home-") ? -0.09 : 0;
        this.place(this.tokenEls[p][t], [cell[0] + jy, cell[1] + jx], scale, dy);
      }
    }

    // Paint order: back-to-front by board row, so a goti standing lower
    // never hides behind the one above it. The active player's gotis are
    // always drawn last — they sit on top of anything they share a cell
    // with or pass over.
    const active = this.game.currentPlayerIndex;
    type Item = { p: number; t: number; row: number };
    const order: Item[] = [];
    for (let p = 0; p < this.game.players.length; p++) {
      for (let t = 0; t < 4; t++) {
        order.push({ p, t, row: this.coordOf(p, t).cell[0] });
      }
    }
    order.sort((a, b) => {
      const pa = a.p === active ? 1 : 0;
      const pb = b.p === active ? 1 : 0;
      if (pa !== pb) return pa - pb;
      return a.row - b.row;
    });
    order.forEach((item) =>
      this.tokenLayer.appendChild(this.tokenEls[item.p][item.t]),
    );
    this.updateSidePanels();
  }

  private snapshotPositions(): string {
    return JSON.stringify(
      this.game.players.map((pl) => pl.tokens.map((t) => [t.state, t.pathIndex, t.stretchIndex])),
    );
  }

  private async animateMove(move: Move): Promise<void> {
    const before: [string, number, number][][] = this.snapshot
      ? JSON.parse(this.snapshot)
      : [];
    const el = this.tokenEls[move.playerIndex][move.tokenIndex];
    this.tokenLayer.appendChild(el); // bring moving token to front
    const color = this.cfg.colors[move.playerIndex];

    // Walking out of base is a single hop onto the start cell
    // (the rules place the token there; it must not walk the track).
    if (move.from.state === TokenState.HOME) {
      audio.play("token_exit"); // dedicated yard-exit sound, not the walk
      this.place(el, this.stepCoord(move.playerIndex, move.tokenIndex, move.to));
      await sleep(260);
    } else {
      // Walk one step at a time through logical positions.
      let cur: TokenPosition = move.from;
      for (let step = 0; step < move.diceValue; step++) {
        cur = this.game.ruleEngine.calculateNewPosition(cur, 1 as DiceValue, color);
        const cell = this.stepCoord(move.playerIndex, move.tokenIndex, cur);
        this.place(el, cell);
        audio.play("token_move");
        await sleep(165); // > the 150ms CSS step so transitions never snap
      }
    }

    // Landing cues based on where the goti ended up.
    if (
      move.from.state === TokenState.ON_PATH &&
      move.to.state === TokenState.HOME_STRETCH
    ) {
      audio.play("stretch_enter"); // entering the winning lane
    } else if (
      move.to.state === TokenState.ON_PATH &&
      SAFE_POSITIONS.includes(move.to.pathIndex)
    ) {
      audio.play("safe_land"); // landed on a star/safe cell
    }

    // Send captured victims home with a quick hop.
    if (before.length > 0) {
      const after: [string, number, number][][] = JSON.parse(this.snapshotPositions());
      for (let p = 0; p < after.length; p++) {
        for (let t = 0; t < 4; t++) {
          const [stB] = before[p]?.[t] ?? ["x"];
          const [stA] = after[p][t];
          if (stB === TokenState.ON_PATH && stA === TokenState.HOME) {
            // Count the kill for the mover and the death for the victim.
            this.kills[move.playerIndex] = (this.kills[move.playerIndex] ?? 0) + 1;
            this.deaths[p] = (this.deaths[p] ?? 0) + 1;
            const victim = this.tokenEls[p][t];
            victim.classList.add("token--captured");
            this.place(victim, this.coordOf(p, t).cell, 1, -0.09);
            await sleep(280);
            victim.classList.remove("token--captured");
          }
        }
      }
    }

    this.syncAll();
  }

  /** Coordinate while stepping through intermediate positions. */
  private stepCoord(playerIdx: number, tokenIdx: number, pos: TokenPosition): Cell {
    if (pos.state === TokenState.ON_PATH) {
      const [r, c] = PATH_COORDS[pos.pathIndex];
      return [r + 0.5, c + 0.5];
    }
    if (pos.state === TokenState.HOME_STRETCH) {
      const [r, c] = STRETCH_COORDS[this.cfg.colors[playerIdx]][pos.stretchIndex];
      return [r + 0.5, c + 0.5];
    }
    if (pos.state === TokenState.FINISHED) {
      return FINISHED_SPOT[this.cfg.colors[playerIdx]];
    }
    return this.coordOf(playerIdx, tokenIdx).cell;
  }

  /* ---------------- highlights ---------------- */

  private highlightMoves(moves: number[]): void {
    const p = this.game.currentPlayerIndex;
    // A 6 is the big moment: stronger glow + size pulse. Other rolls
    // that still allow a move get the soft version.
    const isSix = this.game.diceValue === 6;
    moves.forEach((t) => {
      this.tokenEls[p][t].classList.add("token--movable");
      if (isSix) this.tokenEls[p][t].classList.add("token--six");
    });
  }

  private clearHighlights(): void {
    this.tokenEls.flat().forEach((el) =>
      el.classList.remove("token--movable", "token--six"),
    );
    this.validMoves = [];
  }

  /* ---------------- HUD ---------------- */

  /** One corner tray: dice box + kill/death counters + turn arrow. */
  private trayHtml(color: PlayerColor, side: "left" | "right"): string {
    return `
      <div class="tray tray--${side}" data-color="${slug(color)}" style="--tray:${FILL[color]}" hidden>
        <span class="tray__arrow" aria-hidden="true">
          <svg viewBox="0 0 24 16">
            <path d="M2 2h20l-10 12z" fill="#f5c542" stroke="#b8860b"
                  stroke-width="1.6" stroke-linejoin="round"/>
          </svg>
        </span>
        <div class="tray__box"><div class="tray__slot"></div></div>
        <div class="tray__stats">
          <span class="tray__stat" title="Captures">${SWORDS_SVG}<b class="js-kills">0</b></span>
          <span class="tray__stat" title="Times captured">${SKULL_SVG}<b class="js-deaths">0</b></span>
        </div>
      </div>`;
  }

  /** Dice position, active glow, arrows, kill/death counters, base %. */
  private updateSidePanels(): void {
    if (this.game.players.length === 0) return; // match not started yet
    const active = this.game.currentPlayerIndex;
    const playing = this.game.phase === GamePhase.PLAYING;

    (Object.keys(BASE_ORIGIN) as PlayerColor[]).forEach((color) => {
      const i = this.cfg.colors.indexOf(color);
      const tray = this.trays.get(color);
      const pct = this.progressEls[color];
      if (i < 0) {
        if (tray) tray.hidden = true;
        if (pct) (pct as unknown as HTMLElement).style.display = "none";
        return;
      }
      if (tray) {
        tray.hidden = false;
        tray.classList.toggle("is-active", playing && i === active);
        tray.querySelector(".js-kills")!.textContent = String(this.kills[i] ?? 0);
        tray.querySelector(".js-deaths")!.textContent = String(this.deaths[i] ?? 0);
      }
      if (pct) {
        (pct as unknown as HTMLElement).style.display = "";
        const t = pct.querySelector("text");
        if (t) t.textContent = `${this.progressOf(i)}%`;
      }
    });

    // The dice box follows the turn.
    if (active >= 0 && active < this.cfg.colors.length) {
      const slot = this.trays.get(this.cfg.colors[active])?.querySelector(".tray__slot");
      if (slot && this.diceBtn.parentElement !== slot) slot.appendChild(this.diceBtn);
    }
  }

  /** 0–100% how far a player's four gotis have travelled. Captures
   *  knock the victim's number back down automatically. */
  private progressOf(i: number): number {
    const start = PLAYER_START_POSITIONS[this.cfg.colors[i]];
    const max = PATH_LENGTH + 6; // full lap + win lane
    let sum = 0;
    for (const t of this.game.players[i].tokens) {
      if (t.state === TokenState.ON_PATH) {
        sum += (t.pathIndex - start + PATH_LENGTH) % PATH_LENGTH;
      } else if (t.state === TokenState.HOME_STRETCH) {
        sum += PATH_LENGTH + t.stretchIndex;
      } else if (t.state === TokenState.FINISHED) {
        sum += max;
      }
    }
    return Math.round((sum / (max * 4)) * 100);
  }

  private setStatus(text: string): void {
    this.statusEl.textContent = text;
  }

  private setDiceEnabled(on: boolean): void {
    this.diceBtn.disabled = !on;
  }

  /* ---------------- dice ---------------- */

  private startDiceSpin(): void {
    window.clearInterval(this.rollInterval);
    // 3D die is tumbling already — churning the hidden SVG face underneath
    // (innerHTML every 80ms) only costs frames on old phones. SVG shuffle
    // is the fallback path for when three.js failed to load.
    if (this.dice3d && this.diceBtn.classList.contains("has-3d")) return;
    this.diceFace.classList.add("is-spinning");
    this.rollInterval = window.setInterval(() => {
      const v = (Math.floor(Math.random() * 6) + 1) as DiceValue;
      this.diceFace.innerHTML = dicePips(faceFor(v));
    }, 80);
  }

  private stopDiceSpin(value?: DiceValue): void {
    window.clearInterval(this.rollInterval);
    this.diceFace.classList.remove("is-spinning");
    if (value !== undefined) this.diceFace.innerHTML = dicePips(faceFor(value));
  }

  /* ---------------- results & rankings ---------------- */

  private static RANK_LABEL = ["1st", "2nd", "3rd", "4th"] as const;

  /** Last place is always "Loser" regardless of player count (2P/3P/4P). */
  private rankLabel(i: number): string {
    const total = this.cfg.colors.length;
    if (i === total - 1) return "Loser";
    return GameScreen.RANK_LABEL[i] ?? `${i + 1}th`;
  }
  private rankLabelFor(total: number, i: number): string {
    if (i === total - 1) return "Loser";
    return GameScreen.RANK_LABEL[i] ?? `${i + 1}th`;
  }

  /** King crown SVG — gold with red top gem, blue side gems, red diamond center (like new win ref). */
  private static crownSvg(size: number): string {
    return `<svg viewBox="0 0 72 54" width="${size}" aria-hidden="true" style="filter:drop-shadow(0 6px 14px rgba(0,0,0,0.18))">
      <defs><radialGradient id="crGlow" cx="50%" cy="35%" r="60%"><stop offset="0" stop-color="#ffffff" stop-opacity="0.85"/><stop offset="1" stop-color="#f5c542" stop-opacity="0"/></radialGradient></defs>
      <ellipse cx="36" cy="46" rx="22" ry="5" fill="rgba(0,0,0,0.18)"/>
      <path d="M8 36 L6 14 L20 26 L36 6 L52 26 L66 14 L64 36 Z" fill="#f5c542" stroke="#b8860b" stroke-width="2.2" stroke-linejoin="round"/>
      <rect x="10" y="36" width="52" height="8" rx="3" fill="#c8951d" stroke="#8a5f0f" stroke-width="1.2"/>
      <rect x="10" y="36" width="52" height="4.5" rx="2" fill="#ffe9a8" opacity="0.9"/>
      <polygon points="36,18 32,24 36,30 40,24" fill="#e53935" stroke="#8a1a0f" stroke-width="1" stroke-linejoin="round"/>
      <polygon points="36,19.5 33,23 36,28 39,23" fill="#ff6b6b"/>
      <circle cx="36" cy="6" r="5" fill="#e53935" stroke="#8a1a0f" stroke-width="1.4"/><circle cx="36" cy="6" r="2" fill="#ff8a80"/>
      <circle cx="6" cy="14" r="4.2" fill="#2f6fd6" stroke="#1b2470" stroke-width="1.2"/><circle cx="6" cy="14" r="1.6" fill="#7fb3ff"/>
      <circle cx="66" cy="14" r="4.2" fill="#27a84f" stroke="#145e2a" stroke-width="1.2"/><circle cx="66" cy="14" r="1.6" fill="#7ed957"/>
      <circle cx="20" cy="26" r="2.2" fill="#ffe08a" stroke="#b8860b" stroke-width="0.9"/>
      <circle cx="52" cy="26" r="2.2" fill="#ffe08a" stroke="#b8860b" stroke-width="0.9"/>
    </svg>`;
  }

  /** Ask before destructive actions (restart/home). */
  private confirmAction(msg: string, action: () => void): void {
    this.confirmEl.querySelector(".js-confirm-msg")!.textContent = msg;
    this.confirmCb = action;
    this.confirmEl.hidden = false;
  }

  private makeBtn(label: string, cls: string, onClick: () => void, icon: "arrow" | "restart" | "home" | "none" = "none"): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = `btn ${cls}`;
    const iconSvg = icon === "arrow"
      ? `<span class="btn__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg></span>`
      : icon === "restart"
        ? `<span class="btn__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12a8 8 0 1 1-2.34-5.66"/><path d="M20 4v6h-6"/></svg></span>`
        : icon === "home"
          ? `<span class="btn__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10L12 3l9 7"/><path d="M9 21V11h6v10"/></svg></span>`
          : "";
    b.innerHTML = `<span>${label}</span>${iconSvg}`;
    b.addEventListener("click", onClick);
    return b;
  }

  /**
   * Mid-game placement (3–4 players): celebrate the finisher, then let
   * the rest keep playing. Restart asks for confirmation.
   */
  private showPlacementCard(playerIdx: number, rank: number): void {
    this.paused = true;
    window.clearTimeout(this.aiTimer);
    window.clearTimeout(this.placementAutoTimer);
    const label = this.rankLabelFor(this.cfg.colors.length, rank - 1);
    const isChampion = rank === 1;
    const isLoser = label === "Loser";
    // 2nd/3rd podium keeps trophy art (1st keeps crown) — but all podium
    // finishes should feel like a win, not silent. Fixes 4P 2nd win no sound.
    const podium = !isLoser;
    this.resultEl.querySelector<HTMLElement>(".js-result-art")!.innerHTML =
      isChampion ? GameScreen.crownSvg(92) : this.trophySvg(rank);
    const titleEl = this.resultEl.querySelector<HTMLElement>(".js-result-title")!;
    titleEl.innerHTML = isChampion
      ? `King <span class="t-orange">of the</span> Board!`
      : isLoser ? `Loser!` : `${label} Place!`;
    titleEl.style.color = isChampion ? "#1b2a6b" : "";
    titleEl.style.background = "none";
    titleEl.style.webkitTextFillColor = isChampion ? "#1b2a6b" : "";
    const nameEl = this.resultEl.querySelector<HTMLElement>(".js-result-name")!;
    nameEl.innerHTML = isChampion
      ? `⭐ ${this.escapeHtml(this.nameOf(playerIdx))} finished 1st ⭐`
      : isLoser ? `${this.escapeHtml(this.nameOf(playerIdx))} — Loser` : `${this.escapeHtml(this.nameOf(playerIdx))} finished ${label}`;
    // Bold visible pill with goti colour so player is instantly recognizable.
    {
      const col = this.cfg.colors[playerIdx];
      const fill = FILL[col];
      nameEl.style.background = fill;
      nameEl.style.color = "#fff";
      nameEl.style.borderColor = shade(fill, -0.22);
      nameEl.classList.add("winner-card__name--with-color");
    }
    this.resultEl.querySelector<HTMLElement>(".js-standings")!.innerHTML = "";
    const actions = this.resultEl.querySelector<HTMLElement>(".js-result-actions")!;
    actions.innerHTML = "";
    actions.appendChild(this.makeBtn("CONTINUE", "btn--green", () => this.resumeGame(), "arrow"));
    actions.appendChild(this.makeBtn("RESTART", "btn--gold", () => this.confirmAction("Restart the match from zero?", () => this.rematch()), "restart"));
    this.resultEl.hidden = false;
    // Podium finishes (1st/2nd in 4P, 1st in 3P) all get win audio + celebration.
    // Previously rank>1 used confirm + 22 confetti which looked silent — fix
    // per user report: 4P 2nd win had no sound/celebration.
    if (podium) {
      audio.play("win");
      audio.playWinLoop();
    } else {
      audio.play("confirm");
    }
    // Keep crown tint for 1st, but give 2nd/3rd a strong celebration too (32 pieces
    // instead of 22) so board clearly celebrates. Computer and human share path.
    this.launchCelebration(this.cfg.colors[playerIdx], isChampion);
    // Boost 2nd place burst to be visible even when not champion
    if (podium && !isChampion) {
      const burst = this.celebrationEl?.querySelector<HTMLElement>(".celebration-burst");
      if (burst) {
        const tint = FILL[this.cfg.colors[playerIdx]] ?? "#f5c542";
        burst.style.boxShadow = `0 0 40px ${tint}55`;
      }
    }
    window.clearTimeout(this.placementAutoTimer);
    // AI winner (or all-AI game) has no human to tap CONTINUE — auto-continue after a short view time.
    const winnerIsAI = this.game.players[playerIdx]?.config.isAI === true;
    const allAI = this.cfg.isAI.every(v => v);
    if (winnerIsAI || allAI) {
      this.placementAutoTimer = window.setTimeout(() => {
        if (!this.resultEl.hidden) this.resumeGame();
      }, 2200);
    }
  }

  /** Final screen: standings for everyone. 2P/3P finish immediately — no CONTINUE, full list. */
  private showFinalStandings(rankings: number[]): void {
    this.state = "over";
    this.paused = true;
    this.clearHighlights();
    this.setDiceEnabled(false);
    this.updateRankBadges();

    const champ = rankings[0] ?? 0;
    this.resultEl.querySelector<HTMLElement>(".js-result-art")!.innerHTML = GameScreen.crownSvg(92);
    const titleEl2 = this.resultEl.querySelector<HTMLElement>(".js-result-title")!;
    titleEl2.innerHTML = `King <span class="t-orange">of the</span> Board!`;
    titleEl2.style.color = "#1b2a6b"; titleEl2.style.background = "none"; (titleEl2.style as any).webkitTextFillColor = "#1b2a6b";
    const nameEl2 = this.resultEl.querySelector<HTMLElement>(".js-result-name")!;
    nameEl2.innerHTML = `⭐ ${this.escapeHtml(this.nameOf(champ))} finished 1st ⭐`;
    {
      const champCol = this.cfg.colors[champ];
      const champFill = FILL[champCol];
      nameEl2.style.background = champFill;
      nameEl2.style.color = "#fff";
      nameEl2.style.borderColor = shade(champFill, -0.22);
      nameEl2.classList.add("winner-card__name--with-color");
    }

    // Full standings with goti colour dot so even Loser is instantly recognizable.
    const list = this.resultEl.querySelector<HTMLElement>(".js-standings")!;
    list.innerHTML = rankings
      .map((playerIdx, i) => {
        const col = this.cfg.colors[playerIdx];
        const label = this.rankLabelFor(rankings.length, i);
        const isLoser = label === "Loser";
        const medal = isLoser ? "Loser" : label;
        return `<div class="standings__row" data-rank="${isLoser ? 4 : i + 1}" ${isLoser ? `data-loser="true"` : ""}>
          <span class="standings__dot" style="background:${FILL[col]}"></span>
          <span class="standings__medal">${medal}</span>
          <span class="standings__name">${this.escapeHtml(this.nameOf(playerIdx))}</span>
          ${i === 0 ? `<span class="standings__crown">👑</span>` : ""}
        </div>`;
      })
      .join("");

    const actions = this.resultEl.querySelector<HTMLElement>(".js-result-actions")!;
    actions.innerHTML = "";
    actions.appendChild(this.makeBtn("RESTART", "btn--green", () => this.confirmAction("Restart the match from zero?", () => this.rematch()), "restart"));
    actions.appendChild(this.makeBtn("HOME", "btn--gold", () => this.confirmAction("Quit the match and go home?", () => this.onExitClick()), "home"));
    this.resultEl.hidden = false;
    this.launchCelebration(this.cfg.colors[champ], true);
    // Re-sync board after overlay so yard badges align (fixes 3P bottom-right glitch).
    window.setTimeout(() => this.syncAll(), 60);
  }

  /** Small trophy with the placement number, for non-champion cards. */
  private trophySvg(rank: number): string {
    const colors = ["#f5c542", "#c0c0c8", "#cd7f32", "#8f96a8"];
    const c = colors[rank - 1] ?? colors[3];
    return `<svg viewBox="0 0 64 64" width="72" aria-hidden="true">
      <path d="M20 12h24v10a12 12 0 0 1-24 0z" fill="${c}"/>
      <path d="M20 15h-7a1 1 0 0 0-1 1c0 5.5 3.2 8.8 7.4 10" fill="none" stroke="${c}" stroke-width="3.4"/>
      <path d="M44 15h7a1 1 0 0 1 1 1c0 5.5-3.2 8.8-7.4 10" fill="none" stroke="${c}" stroke-width="3.4"/>
      <rect x="29" y="33" width="6" height="8" fill="${c}" opacity=".7"/>
      <rect x="21" y="41" width="22" height="6" rx="2.4" fill="${c}"/>
      <text x="32" y="21.8" text-anchor="middle" font-size="11" font-weight="900"
            fill="rgba(0,0,0,.55)">${rank}</text>
    </svg>`;
  }

  /** Close the placement card and resume play. */
  private resumeGame(): void {
    audio.stopWinLoop();
    this.clearCelebration();
    this.resultEl.hidden = true;
    window.clearTimeout(this.placementAutoTimer);
    this.game.resumeAfterPlacement();
    this.paused = false;
    this.syncAll();
    const idx = this.game.currentPlayerIndex;
    if (this.game.phase === GamePhase.PLAYING) {
      if (!this.isHuman(idx)) {
        this.scheduleAiRoll(600);
      } else {
        this.armRoll();
      }
    }
  }

  /* ---------------- winner celebration (isolated, no font impact) ---------------- */

  private launchCelebration(winnerColor: PlayerColor, isChampion: boolean): void {
    this.clearCelebration();
    const overlay = this.resultEl;
    const card = overlay.querySelector<HTMLElement>(".winner-card");
    if (card) card.classList.add("is-celebrating");

    const layer = document.createElement("div");
    layer.className = "celebration-layer--overlay";
    layer.setAttribute("aria-hidden", "true");

    // light burst behind card
    const burst = document.createElement("div");
    burst.className = "celebration-burst";
    // tint burst slightly with winner color for personalization (no font touch)
    if (isChampion) {
      const tint = FILL[winnerColor] ?? "#f5c542";
      // keep burst readable — just a soft outer glow, not a solid recolor
      burst.style.boxShadow = `0 0 40px ${tint}55`;
    }
    layer.appendChild(burst);
    overlay.appendChild(layer);
    this.celebrationEl = layer;

    // confetti — 42 pieces, winner color weighted + gold
    const palette = [
      "#f5c542", "#ffe08a", "#ffffff",
      FILL[winnerColor] ?? "#f5c542",
      FILL[winnerColor] ?? "#ff6b6b",
      "#7ed957", "#4a5fd5",
    ];
    const N = isChampion ? 42 : 32;
    for (let i = 0; i < N; i++) {
      const el = document.createElement("span");
      const isDot = Math.random() < 0.22;
      const isRibbon = !isDot && Math.random() < 0.28;
      el.className = isDot ? "cf cf--dot" : isRibbon ? "cf cf--ribbon" : "cf";
      const col = palette[Math.floor(Math.random() * palette.length)];
      const left = Math.random() * 100;
      const dur = 2.2 + Math.random() * 1.8;
      const delay = Math.random() * 0.55;
      const drift = (Math.random() - 0.5) * 120;
      const rot = 360 + Math.random() * 720;
      const w = isDot ? 8 + Math.random() * 5 : 8 + Math.random() * 6;
      const h = isDot ? w : isRibbon ? 16 + Math.random() * 8 : 10 + Math.random() * 6;
      el.style.cssText = `left:${left.toFixed(2)}%;background:${col};width:${w.toFixed(1)}px;height:${h.toFixed(1)}px;--x:${(Math.random()*20-10).toFixed(1)}px;--drift:${drift.toFixed(0)}px;--dur:${dur.toFixed(2)}s;--delay:${delay.toFixed(2)}s;--rot:${rot.toFixed(0)}deg;`;
      if (Math.random() < 0.35) el.style.border = "1px solid rgba(255,255,255,0.55)";
      layer.appendChild(el);
    }

    // sparkles around trophy — 6 little stars popping
    const sparklePos = [
      { l: "18%", t: "12%" }, { l: "78%", t: "10%" },
      { l: "10%", t: "52%" }, { l: "86%", t: "48%" },
      { l: "24%", t: "82%" }, { l: "72%", t: "84%" },
    ];
    sparklePos.forEach((p, idx) => {
      const s = document.createElement("span");
      s.className = "sparkle";
      s.style.left = p.l;
      s.style.top = p.t;
      s.style.animationDelay = `${0.15 + idx * 0.12}s`;
      s.style.background = idx % 2 === 0 ? "#ffe08a" : "#ffffff";
      // attach to card so sparkles stay with the trophy even on small screens
      card?.appendChild(s);
      // auto-remove after animation
      const tid = window.setTimeout(() => s.remove(), 1700 + idx * 120);
      this.celebrationTimers.push(tid);
    });

    // auto-clean confetti after ~4.2s but keep card glow; layer stays until next close
    // Do NOT auto-hide — user dismisses via CONTINUE/RESTART/HOME. Confetti just fades via CSS.
  }

  private clearCelebration(): void {
    this.celebrationTimers.forEach(id => window.clearTimeout(id));
    this.celebrationTimers = [];
    if (this.celebrationEl) {
      this.celebrationEl.remove();
      this.celebrationEl = null;
    }
    // also remove any stray sparkles/burst left inside card (on rematch etc.)
    this.resultEl.querySelectorAll(".sparkle, .celebration-burst").forEach(el => el.remove());
    this.resultEl.querySelector<HTMLElement>(".winner-card")?.classList.remove("is-celebrating");
  }

  /** Winning STYLE in box — like the photo: grey box + big gold crown with bold number. Last is "Loser". */
  private updateRankBadges(): void {
    this.tokenLayer.querySelectorAll(".rank-badge").forEach((el) => el.remove());
    const total = this.cfg.colors.length;
    this.game.rankings.forEach((playerIdx, i) => {
      const color = this.cfg.colors[playerIdx];
      const [r, c] = BASE_ORIGIN[color];
      const label = this.rankLabelFor(total, i);
      const isLoser = label === "Loser";
      const rankNum = isLoser ? "✕" : label.replace(/\D/g, "") || label;
      // Crown color per podium: 1st gold, 2nd silver, 3rd bronze, loser grey
      const crownFill = isLoser ? "#8f96a8" : (["#f5c542", "#c0c0c8", "#cd7f32"][i] ?? "#f5c542");
      const crownStroke = isLoser ? "#5a6470" : "#b8860b";
      const cx = c + 3;
      const cy = r + 3;
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.classList.add("rank-badge");
      // Photo style: light grey inset box + big crown centered with bold number
      g.innerHTML = `<g transform="translate(${cx},${cy})">
        <rect x="-1.75" y="-1.75" width="3.5" height="3.5" rx="0.42" fill="#f0f2f7" stroke="${FILL[color]}" stroke-width="0.09" opacity="0.98"/>
        <rect x="-1.55" y="-1.55" width="3.1" height="3.1" rx="0.28" fill="none" stroke="rgba(16,23,63,.18)" stroke-width="0.05"/>
        <g transform="translate(0,0.18) scale(0.038)">
          <!-- Blue discs behind crown (as in photo) -->
          <circle cx="-22" cy="-10" r="14" fill="#3a7bd5" opacity="0.95"/>
          <circle cx="22" cy="-10" r="14" fill="#3a7bd5" opacity="0.95"/>
          <circle cx="-24" cy="18" r="11" fill="#3a7bd5" opacity="0.95"/>
          <circle cx="24" cy="18" r="11" fill="#3a7bd5" opacity="0.95"/>
          <!-- Crown body -->
          <path d="M -36 14 L -44 -14 L -26 2 L -10 -26 L 0 -36 L 10 -26 L 26 2 L 44 -14 L 36 14 Z"
                fill="${crownFill}" stroke="${crownStroke}" stroke-width="2.2" stroke-linejoin="round"/>
          <rect x="-30" y="14" width="60" height="14" rx="2" fill="${crownFill}" stroke="${crownStroke}" stroke-width="2"/>
          <!-- Tip jewels (gold) -->
          <circle cx="-44" cy="-14" r="3.2" fill="#ffe08a" stroke="${crownStroke}" stroke-width="1.2"/>
          <circle cx="-26" cy="2" r="2.2" fill="#ffe08a" stroke="${crownStroke}" stroke-width="1"/>
          <circle cx="0" cy="-36" r="3.8" fill="#ffe9a8" stroke="${crownStroke}" stroke-width="1.2"/>
          <circle cx="26" cy="2" r="2.2" fill="#ffe08a" stroke="${crownStroke}" stroke-width="1"/>
          <circle cx="44" cy="-14" r="3.2" fill="#ffe08a" stroke="${crownStroke}" stroke-width="1.2"/>
          <!-- Front gems (blue) -->
          <circle cx="-16" cy="21" r="4.2" fill="#3a7bd5" stroke="white" stroke-width="1.1"/>
          <circle cx="16" cy="21" r="4.2" fill="#3a7bd5" stroke="white" stroke-width="1.1"/>
          <!-- Bold rank number on crown (black as photo) -->
          <text x="0" y="15" text-anchor="middle" font-family="Nunito, sans-serif" font-weight="900" font-size="30" fill="#0a0a0a" stroke="rgba(255,255,255,.35)" stroke-width="0.8" paint-order="stroke">${rankNum}</text>
        </g>
      </g>`;
      this.tokenLayer.appendChild(g);
    });
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (ch) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!),
    );
  }
}

/** Pip layout per dice value using the shared pip coordinate map. */
function faceFor(v: DiceValue): number[] {
  switch (v) {
    case 1: return [1];
    case 2: return [2, 3];
    case 3: return [2, 1, 3];
    case 4: return [2, 4, 3, 5];
    case 5: return [2, 4, 1, 3, 5];
    case 6: return [2, 4, 6, 3, 5, 7];
  }
}
